import { valueConsistent } from "./candidateSelection";
import { buildUndoPayload } from "./standardizationDiff";

// Fields the base candidate fills from (locality). addr1 is handled separately
// because base-retain canonicalizes the street and re-appends the user's tail.
const LOCALITY_FIELDS = ["city", "state", "zip", "country"];

/**
 * Compute the commit for the **base-retain unverifiable** case:
 * the user typed a street Smarty recognized plus an appended unit/tail Smarty
 * can't complete (e.g. "13103 Hitching Post Road apt 4"). Smarty returns no
 * row for the full string, but a retained candidate's addr1 is a prefix of what
 * was typed.
 *
 * The recognized base gets the same canonicalization a fully-verified address
 * would ("Road" → "Rd", casing, directionals) and the user's unit/tail is
 * re-appended verbatim, so `addr1` becomes the canonical base + " " + tail. The
 * locality (city/state/zip/country) is filled from the candidate, EXCEPT a field
 * the user edited to a value the candidate would contradict — that typed value is
 * kept (same rule as the forward path). The verdict still settles unverifiable —
 * the appended tail isn't a Smarty leaf.
 *
 * Shared by every base-retain site (the inline hook resolve, and the
 * hold-surface commit handlers) so canonicalization and the
 * street-keyed standardization decision are identical everywhere.
 *
 * @param {string} typed - the full addr1 the user typed (street + appended tail)
 * @param {object} candidate - the retained base candidate (addr1 + locality)
 * @param {string} appendedTail - the trimmed tail past the base ("apt 4")
 * @param {object} currentValue - the form's current subfields (for the typed locality)
 * @param {object} editedFields - which subfields the user typed into (city/state/zip)
 * @returns {{
 *   addr1: string,            // canonical base + " " + tail
 *   fill: object,             // { addr1, city, state, zip, country } to commit
 *   streetStandardized: boolean, // did canonicalizing change the typed street?
 *   undoPayload: object,      // restore the FULL original typed string + locality
 * }}
 */
export default function buildBaseRetainCommit(
  typed,
  candidate,
  appendedTail,
  currentValue = {},
  editedFields = {}
) {
  const canonicalBase = (candidate.addr1 || "").trim();
  const tail = (appendedTail || "").trim();
  const addr1 = tail ? `${canonicalBase} ${tail}` : canonicalBase;

  // Locality from the candidate, but PRESERVE a value the user typed that the
  // candidate would overwrite — the same rule the forward path enforces via
  // candidateConsistentWithForm. Fill from the candidate when the field is empty,
  // consistent (a prefix/completion), or stale (un-edited); keep the typed value
  // only when the user EDITED the field AND it contradicts the candidate. Never
  // blank a field the candidate doesn't carry.
  const fill = { addr1 };
  LOCALITY_FIELDS.forEach((fieldName) => {
    const candidateFieldValue = candidate[fieldName];
    if (candidateFieldValue === undefined || candidateFieldValue === "") return;
    const typedValue = currentValue[fieldName];
    if (
      editedFields[fieldName] &&
      !valueConsistent(typedValue, candidateFieldValue)
    ) {
      fill[fieldName] = typedValue;
      return;
    }
    fill[fieldName] = candidateFieldValue;
  });

  // Key the standardization decision on the STREET tokens only, never on the
  // auto-filled locality. The typed street is `typed` with the appended tail
  // removed (by token count — the tail aligns to the end). Completion of
  // city/state/zip is NOT standardization, so it must not drive the undo notice.
  const typedStreet = stripTrailingTailTokens(typed, tail);
  const streetStandardized =
    typedStreet.trim().toLowerCase() !== canonicalBase.toLowerCase();

  // Undo restores the FULL original typed string (street + unit verbatim), with
  // the candidate's locality kept for fields the user never typed (so undo
  // doesn't blank an auto-filled city/state/zip). buildUndoPayload overlays the
  // typed fields onto the filled address; pass the original typed addr1 as the
  // typed street so undo restores "…Road apt 4", not the canonical "…Rd apt 4".
  const undoPayload = buildUndoPayload({ addr1: typed }, fill);

  return { addr1, fill, streetStandardized, undoPayload };
}

// Remove the last N whitespace-delimited tokens of `typed`, where N is the
// token count of `tail`. The tail aligns to the end of the typed string
// (it's the suffix past the recognized base), so dropping that many trailing
// tokens yields the typed street as the user spelled it.
function stripTrailingTailTokens(typed, tail) {
  const typedTokens = (typed || "").split(/\s+/).filter(Boolean);
  const tailTokens = (tail || "").split(/\s+/).filter(Boolean);
  if (!tailTokens.length) return typed || "";
  return typedTokens.slice(0, typedTokens.length - tailTokens.length).join(" ");
}
